import React, { useState, useEffect, useContext } from 'react';
import { Container } from 'reactstrap';
import CourseNewCard from '../Card/CourseNewCard';
import Amplify, { Auth } from 'aws-amplify';
import config from '../../aws-exports';
import { UserContext } from '../../components/UserContext';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { cx, css } from '@emotion/css';
import * as assignmentStyles from '../assignments/assignments.style';
import mathsImg from '../../assets/gen_images/mathematics.png';

Amplify.configure(config);

const pageBgStyles = css`
  background-color: #fdfbf7;
  background-image: 
    radial-gradient(at 100% 0%, rgba(241, 102, 2, 0.04) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(244, 203, 14, 0.02) 0px, transparent 50%);
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const headerStyles = {
  container: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
    border-bottom: 1px solid rgba(241, 102, 2, 0.08);
    padding-bottom: 1.2rem;

    @media (max-width: 991px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1.2rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.2rem;
    }
  `,
  leftCol: css`
    flex: 1;
    max-width: 850px;
  `,
  topLabel: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(241, 102, 2, 0.7);
    margin-bottom: 0.6rem;
    display: block;
  `,
  heading: css`
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 2.4rem;
    font-weight: 800;
    font-style: italic;
    line-height: 1.05;
    margin: 0 0 0.5rem 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;

    .orange-part {
      color: #f16602;
      margin-right: 0.6rem;
      display: inline-block;
    }

    .outline-part {
      color: transparent;
      -webkit-text-stroke: 1.5px rgba(241, 102, 2, 0.25);
      font-weight: 900;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
      
      .outline-part {
        -webkit-text-stroke: 1px rgba(241, 102, 2, 0.25);
      }
    }
  `,
  description: css`
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    color: #554a42;
    line-height: 1.6;
    margin: 0;
    max-width: 700px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  `
};

function CoursesCatScreen(props) {
  const [events_category, set_events_category] = useState([]);
  const { user, setUser } = useContext(UserContext);
  const [loadedcategories, set_loadedcategories] = useState(false);

  useEffect(() => {
    getToken();
    document.title = 'Course Categories | QuizWizard';
  }, []);

  const getToken = async () => {
    try {
      const userObj = await Auth.currentAuthenticatedUser();
      const res = await Auth.currentSession();
      const jwt = res.getAccessToken().getJwtToken();
      const username = userObj.username.includes("Google_") ? userObj.attributes['email'] : userObj.username;
      
      getuserDetails(username, jwt);
      getEventsfromDB(username, jwt);
    } catch (error) {
      console.error("Auth error:", error);
      Auth.signOut();
    }
  };

  const getEventsfromDB = async (username, jwt) => {
    const data1 = [{ id: 3, category: 'Mathematics', count: 2, level: 12 }];
    set_events_category(data1);
    set_loadedcategories(true);
  };

  const getuserDetails = async (username, jwt) => {
    try {
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: jwt },
        body: JSON.stringify({ username: username }),
      };
      const response = await fetch(`https://api.quizwizard.net/quiz/getuser`, requestOptions);
      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  return (
    <div className={cx(pageBgStyles)}>
      <Container style={{ marginTop: '2.5rem' }}>
        {/* Curated Header Layout for Course Categories Page */}
        <div className={cx(headerStyles.container)}>
          <div className={cx(headerStyles.leftCol)}>
            <span className={cx(headerStyles.topLabel)}>CURRICULUM EXPLORATION</span>
            <h1 className={cx(headerStyles.heading)}>
              <span className="orange-part">Course</span>
              <span className="outline-part">Categories</span>
            </h1>
            <p className={cx(headerStyles.description)}>
              Master academic foundations, trace subject curriculums, and advance track progress with dedicated module quizzes.
            </p>
          </div>
        </div>

        {loadedcategories ? (
          <div className={cx(assignmentStyles.challengeGrid)} style={{ padding: '0 0 80px 0' }}>
            {events_category.map((eventcategory) => (
              <CourseNewCard
                key={eventcategory.id}
                title={eventcategory.category}
                imgsrc={mathsImg}
                levels={[eventcategory.level]}
                totalquestions={eventcategory.count}
                event={{ ...eventcategory, name: eventcategory.category }}
                category={eventcategory.category}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <Lottie options={defaultOptions} height={200} width={200} />
            <p className="mt-3">Loading categories...</p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default CoursesCatScreen;
